import type { CustomFormErrors, FormErrors, FormErrorsWithArray } from 'redux-form';

import type { W_Job_FindInvoiceById_VM } from 'ab-api/web/job/findInvoiceById';
import type { W_Job_UpdateInvoicedInvoice_RM } from 'ab-api/web/job/updateInvoicedInvoice';

import type InstallmentRM from 'ab-requestModels/invoice/installment/installment.requestModel';

import { getId } from '@acceligentllc/shared/utils/array';
import { filterContactMethod } from '@acceligentllc/shared/utils/contact';
import { isValidEmail } from '@acceligentllc/shared/utils/email';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { EmailTypes, EmailTypesArray } from '@acceligentllc/shared/enums/contactMethodType';
import type { PhoneTypes } from '@acceligentllc/shared/enums/contactMethodType';
import type { FileType } from '@acceligentllc/shared/enums/fileType';
import { InvoiceBillingLookupType } from '@acceligentllc/shared/enums/invoiceBillingLookupType';
import type { ResourceStatus } from '@acceligentllc/shared/enums/resourceStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type InvoiceStatusDisplay from 'ab-enums/invoiceStatusDisplay.enum';

class InstallmentFM {
	id: number | string;
	invoiceId: number;
	instNumber: number;
	amount: Nullable<string>;
	/** YYYY-MM-DD */
	datePaid: Nullable<string>;
	note: Nullable<string>;

	static validate = (form: InstallmentFM): FormErrors<InstallmentFM> => {

		const errors: FormErrors<InstallmentFM> = {};

		if (form.amount === null || form.amount === undefined) {
			errors.amount = 'Amount is required';
		}

		if (!form.datePaid) {
			errors.datePaid = 'Date paid is required';
		} else if (form.datePaid && !TimeUtils.isDateInCorrectFormat(form.datePaid, TimeFormat.DB_DATE_ONLY)) {
			errors.datePaid = 'Date paid is not in YYYY-MM-DD format';
		}

		return errors;
	};

	static fromFMtoRM(fm: InstallmentFM): InstallmentRM {

		if (fm.invoiceId === null || fm.invoiceId === undefined || !fm.datePaid || fm.amount === undefined || fm.amount === null) {
			throw new Error('Installment is not filled out correctly');
		}

		return {
			invoiceId: fm.invoiceId,
			amount: fm.amount,
			datePaid: fm.datePaid,
			note: fm.note,
		};
	}
}

interface UserInfoFM {
	accountId?: Nullable<number>;
	userId?: number;
	firstName?: string;
	lastName?: string;
	fullName: string;
}

export class AttachmentFM {
	id: number;
	size: number;
	fileName: string;
	type: FileType;
	status: ResourceStatus;
	storageName: string;
	storageContainer: string;
	/** original version with presigned url */
	originalSrc: string;
	/** ISO_DATETIME */
	lastModifiedAt: string;
	uploadedBy: Nullable<UserInfoFM>;
}

class ContactMethodFM {
	id: number;
	contactId: number;
	type: PhoneTypes | EmailTypes;
	value: string;

	constructor(contactMethod) {
		this.id = contactMethod.id;
		this.contactId = contactMethod.contactId;
		this.type = contactMethod.type;
		this.value = contactMethod.value;
	}

	static toList(contactMethods, type: typeof EmailTypes | typeof PhoneTypes): ContactMethodFM[] {
		return contactMethods
			.filter((_method) => _method && Object.keys(type).includes(_method.type))
			.map((_method) => new ContactMethodFM(_method));
	}
}

class ContactFM {
	id?: number;
	contactId: number;
	title: Nullable<string>;
	fullName: string;
	companyName: Nullable<string>;
	emails: ContactMethodFM[];
	contactEmailIds: number[];

	constructor(_contactLookup) {
		const _contact = _contactLookup?.contact;

		this.id = _contactLookup.id;
		this.contactId = _contactLookup.contactId;
		this.title = _contact?.title;
		this.fullName = _contact?.fullName;
		this.companyName = _contact?.companyName;

		this.emails = ContactMethodFM.toList(_contact?.contactMethods ?? [], EmailTypes);

		this.contactEmailIds = filterContactMethod(_contactLookup.contactLookupMethods, EmailTypesArray);
	}

	static bulkConstructor = (_contactLookups) => _contactLookups.map((__contactLookup) => new ContactFM(__contactLookup));
}

type BillingEmail = {
	type: InvoiceBillingLookupType.EMAIL_ONLY;
	email: string;
};

export type BillingContact = {
	contact: ContactFM;
	type: InvoiceBillingLookupType.CONTACT;
};

type InvoiceBillingContactFM = { id?: number; } & (BillingEmail | BillingContact);

type ContactMethod = {
	id: number;
	type: InvoiceBillingLookupType.CONTACT;
	email: string;
	contactId: number;
	title: Nullable<string>;
	fullName: string;
	companyName: Nullable<string>;
	/** ISO_DATETIME */
	lastReminderSent: Nullable<string>;
	contactMethodId: number;
};

type EmailOnlyInfo = {
	id: number;
	/** ISO_DATETIME */
	lastReminderSent: Nullable<string>;
} & BillingEmail;

type ContactTableInfo = EmailOnlyInfo | ContactMethod;

export class InvoiceFM {
	id: number;
	invoiceCode: string;
	/** MM-DD-YYYY */
	dateCreated: string;
	/** MM-DD-YYYY */
	dueDate: string;
	status: InvoiceStatusDisplay;
	note: Nullable<string>;
	totalAmount: number;
	paidAmount: number;
	outstandingDebt: number;
	retainedAmount: number;
	billingContacts: Nullable<InvoiceBillingContactFM>[];
	sendReminderOnInvoice: boolean;
	excludeInternalUsersFromAutomaticReminders: boolean;
	excludeBillingContactsFromAutomaticReminders: boolean;
	installments: InstallmentFM[];
	attachments: AttachmentFM[];
	uploadedAttachmentIds?: number[];
	uploadingAttachments?: Record<string, true>;
	billingContactMethods: ContactTableInfo[];

	static getAttributeName = (attribute: keyof InvoiceFM) => attribute;

	static recalculatePaidAmount = (installments: InstallmentFM[]) => {
		return installments.reduce((_sum, _inst) => {
			_sum += (+(_inst.amount ?? 0));
			return _sum;
		}, 0);
	};

	static fromVMtoFM(vm: W_Job_FindInvoiceById_VM): InvoiceFM {
		return {
			id: vm.id,
			invoiceCode: vm.invoiceCode,
			status: vm.status,
			dateCreated: TimeUtils.formatDate(vm.dateCreated, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY),
			dueDate: TimeUtils.formatDate(vm.dueDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY),
			totalAmount: vm.totalAmount,
			paidAmount: vm.paidAmount,
			outstandingDebt: vm.outstandingDebt,
			retainedAmount: vm.retainedAmount,
			sendReminderOnInvoice: vm.sendReminderOnInvoice,
			excludeInternalUsersFromAutomaticReminders: vm.excludeInternalUsersFromAutomaticReminders,
			excludeBillingContactsFromAutomaticReminders: vm.excludeBillingContactsFromAutomaticReminders,
			billingContacts: vm.billingContacts?.map((_lookup) => {
				switch (_lookup.type) {
					case InvoiceBillingLookupType.EMAIL_ONLY: {
						return {
							id: _lookup.id,
							type: InvoiceBillingLookupType.EMAIL_ONLY,
							email: _lookup.email,
						};
					}
					case InvoiceBillingLookupType.CONTACT: {
						return {
							id: _lookup.id,
							type: InvoiceBillingLookupType.CONTACT,
							contact: _lookup.contact,
						};
					}
				}
			}) ?? [],
			installments: vm.installments?.reverse().map((_inst, _ind) => {
				return { ..._inst, invoiceId: vm.id, editable: false, instNumber: (_ind + 1) };
			}) ?? [],
			note: vm.note,
			attachments: vm.attachments.map((_att) => { return { ..._att, fileName: _att.name }; }) ?? [],
			uploadedAttachmentIds: [],
			billingContactMethods: vm.billingContactMethods ?? [],
		};
	}

	static fromFMtoRM(fm: InvoiceFM): W_Job_UpdateInvoicedInvoice_RM {
		return {
			id: fm.id,
			invoiceCode: fm.invoiceCode,
			dateCreated: TimeUtils.formatDate(fm.dateCreated, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			dueDate: TimeUtils.formatDate(fm.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			note: fm.note,
			retainedAmount: fm.retainedAmount?.toString() ?? '0',
			excludeInternalUsersFromAutomaticReminders: fm.excludeInternalUsersFromAutomaticReminders,
			excludeBillingContactsFromAutomaticReminders: fm.excludeBillingContactsFromAutomaticReminders,
			installments: fm.installments?.map(InstallmentFM.fromFMtoRM) ?? null,
			uploadedAttachmentIds: fm.uploadedAttachmentIds ?? [],
			attachmentIds: (fm.attachments ?? []).map(getId),
			billingContacts: fm.billingContacts.reduce<W_Job_UpdateInvoicedInvoice_RM['billingContacts']>((_acc, _invoiceFM) => {
				if (!_invoiceFM) {
					return _acc;
				}

				switch (_invoiceFM.type) {
					case InvoiceBillingLookupType.EMAIL_ONLY: {
						_acc.push({
							id: _invoiceFM.id,
							type: InvoiceBillingLookupType.EMAIL_ONLY,
							email: _invoiceFM.email,
						});
						break;
					}
					case InvoiceBillingLookupType.CONTACT: {
						_acc.push({
							id: _invoiceFM.id,
							type: InvoiceBillingLookupType.CONTACT,
							contact: {
								id: _invoiceFM.contact.id,
								contactId: _invoiceFM.contact.contactId,
								contactEmailIds: _invoiceFM.contact.contactEmailIds,
							},
						});
						break;
					}
				}

				return _acc;
			}, [] as W_Job_UpdateInvoicedInvoice_RM['billingContacts']) ?? [],
			totalAmount: fm.totalAmount,
			billingContactMethods: fm.billingContactMethods,
		};
	}

	static _checkBillingContactErrors = (_error) => !Object.keys(_error ?? {}).length;

	static validate = (form: InvoiceFM): FormErrors<InvoiceFM> => {

		const errors: CustomFormErrors<InvoiceFM> = {};

		errors.installments = form?.installments?.reduce((_acc, _entry) => {
			const validatedEntry = InstallmentFM.validate(_entry);
			_acc.push(validatedEntry);
			return _acc;
		}, [] as FormErrors<InstallmentFM>[]);

		if (!!form.uploadingAttachments && Object.keys(form.uploadingAttachments).length) {
			errors.uploadingAttachments = 'Attachment upload in progress.';
		}

		return errors as FormErrors<InvoiceFM>;
	};

	static _hasInvalidBillingContact = (_billingContactError) => {
		return !!_billingContactError ? !!Object.keys(_billingContactError).length : false;
	};

	static saveFormEnabled = (form: InvoiceFM, errors: FormErrorsWithArray<InvoiceFM, string>, areThereInstallmentsInEditMode: boolean): boolean => {
		if (!form) {
			return false;
		}
		if (!form.invoiceCode || !form.dateCreated || !form.dueDate || form.totalAmount === undefined || form.totalAmount === null) {
			return false;
		}

		for (const _billingCOntactMethod of form.billingContactMethods) {
			if (!_billingCOntactMethod) {
				return false;
			} else if (_billingCOntactMethod.email !== '' && !_billingCOntactMethod.email.includes('@')) {
				return false;
			} else if (_billingCOntactMethod.email !== '' && !isValidEmail(_billingCOntactMethod.email)) {
				return false;
			} else if (_billingCOntactMethod.email === '') {
				return false;
			}
		}

		const areBillingContactsInvalid = errors.billingContacts?.some(InvoiceFM._hasInvalidBillingContact);

		if (areBillingContactsInvalid) {
			return false;
		}

		const thereAreAttachmentsInUpload = !!form.uploadingAttachments && Object.keys(form.uploadingAttachments).length;

		return !areThereInstallmentsInEditMode && !thereAreAttachmentsInUpload;
	};

}
